/**
 * AdminLayout — completely isolated from the main app's Layout/Sidebar/Header.
 * Shares ONLY shadcn/ui primitives (pure UI, zero business logic).
 * Can be extracted into a standalone app by moving src/pages/admin/ + src/lib/adminApi.ts.
 */
import {
  Activity,
  BarChart3,
  BookOpen,
  ChevronLeft,
  FileText,
  LogOut,
  Server,
  Settings,
  Shield,
  Users,
  Zap,
} from "lucide-react";
import React from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import { cn } from "../../lib/utils";
import { useAppStore } from "../../store/useAppStore";

interface AdminNavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV_ITEMS: AdminNavItem[] = [
  { label: "Overview",    href: "/admin",             icon: BarChart3 },
  { label: "Users",       href: "/admin/users",        icon: Users },
  { label: "Services",    href: "/admin/services",     icon: Server },
  { label: "Deployments", href: "/admin/deployments",  icon: Zap },
  { label: "System",      href: "/admin/system",       icon: Activity },
  { label: "Audit Log",   href: "/admin/audit",        icon: BookOpen },
];

function getInitials(name?: string, email?: string): string {
  const src = name?.trim() || email?.trim() || "";
  if (!src) return "A";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export function AdminLayout() {
  const { user, logout } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();

  // Gate: only admin users can see this layout
  if (!user || user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate("/auth/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* ── Sidebar ────────────────────────────────────────────────────────── */}
      <aside className="flex w-60 shrink-0 flex-col border-r bg-card">
        {/* Brand */}
        <div className="flex h-14 items-center gap-2.5 border-b px-4">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-destructive/15">
            <Shield className="h-4 w-4 text-destructive" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-none">Admin Console</p>
            <p className="text-2xs text-muted-foreground mt-0.5">Avahana Platform</p>
          </div>
        </div>

        {/* Back to app */}
        <div className="px-3 pt-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs text-muted-foreground"
            asChild
          >
            <Link to="/projects">
              <ChevronLeft className="mr-1.5 h-3.5 w-3.5" />
              Back to app
            </Link>
          </Button>
        </div>

        <Separator className="mt-3" />

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const isActive =
              item.href === "/admin"
                ? location.pathname === "/admin"
                : location.pathname.startsWith(item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-destructive/10 text-destructive before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-full before:bg-destructive"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <Separator />

        {/* Footer: user + logout */}
        <div className="p-3 space-y-1">
          <div className="flex items-center gap-2.5 rounded-md px-2 py-2">
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarFallback className="bg-destructive/10 text-2xs font-medium text-destructive">
                {getInitials(user.name, user.email)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-xs font-medium leading-none">{user.name || user.email}</p>
              <p className="truncate text-2xs text-muted-foreground mt-0.5">Admin</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs text-muted-foreground hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="mr-1.5 h-3.5 w-3.5" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* ── Main content ───────────────────────────────────────────────────── */}
      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Minimal header strip */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b px-6">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Shield className="h-3.5 w-3.5 text-destructive" />
            <span>Super Admin — restricted access</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <Link to="/settings">
                <Settings className="h-4 w-4" />
              </Link>
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <Link to="/admin/audit">
                <FileText className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
